import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Divider,
  Typography,
  Card,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  RobotOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { SourceStory } from '../../types';
import { sourceStoriesApi, CreateSourceStoryRequest, UpdateSourceStoryRequest } from '../../services/api';
import { useAsyncOperation } from '../../hooks';
import StoryConceptEditorModal from './StoryConceptEditorModal';

const { Text } = Typography;

interface SourceStoryModalProps {
  open: boolean;
  editingStory: SourceStory | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const SourceStoryModal: React.FC<SourceStoryModalProps> = ({
  open,
  editingStory,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [isConceptModalVisible, setIsConceptModalVisible] = useState(false);
  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();
  const [isSuggestingAudiences, setIsSuggestingAudiences] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingStory) {
        form.setFieldsValue({
          name: editingStory.name,
          author: editingStory.author,
          link: editingStory.link,
          storyConcept: editingStory.storyConcept,
          targetAudiences: editingStory.targetAudiences || [],
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, editingStory, form]);

  const handleSubmit = async (values: any) => {
    const operation = editingStory
      ? async () => {
          const response = await sourceStoriesApi.updateSourceStory(editingStory.id, values as UpdateSourceStoryRequest);
          return response.data;
        }
      : async () => {
          const response = await sourceStoriesApi.createSourceStory(values as CreateSourceStoryRequest);
          return response.data;
        };

    const result = await executeAsync(
      operation,
      {
        successMessage: editingStory
          ? 'Source story updated successfully!'
          : 'Source story created successfully!'
      }
    );

    if (result) {
      onSuccess();
    }
  };

  const handleSuggestAudiences = async () => {
    const concept = form.getFieldValue('storyConcept');
    if (!concept || !concept.trim()) {
      Modal.warning({
        title: 'Story Concept Required',
        content: 'Please enter a Story Concept before using AI to suggest audiences.',
      });
      return;
    }

    setIsSuggestingAudiences(true);
    try {
      const res = await sourceStoriesApi.suggestTargetAudiences(concept);
      const suggestedAudiences = res.data;
      
      // Decision: Let's replace for now as it's a "suggestion"
      form.setFieldsValue({ targetAudiences: suggestedAudiences });
    } catch (error) {
      console.error('Failed to suggest audiences:', error);
    } finally {
      setIsSuggestingAudiences(false);
    }
  };

  return (
    <>
      <Modal
        title={editingStory ? 'Edit Source Story' : 'Create New Source Story'}
        open={open}
        onCancel={onCancel}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ targetAudiences: [] }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name!' }]}
          >
            <Input placeholder="Enter story name..." />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="author"
              label="Author"
            >
              <Input placeholder="Enter author name..." />
            </Form.Item>

            <Form.Item
              name="link"
              label="Link"
            >
              <Input placeholder="Enter original story link..." />
            </Form.Item>
          </div>

          <Divider>Auto-Story Settings</Divider>
          
          <Form.Item label="Story Concept" required>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Form.Item name="storyConcept" noStyle>
                <Input.TextArea 
                  style={{ display: 'none' }} // Hidden field but kept in form state
                />
              </Form.Item>
              <Button 
                icon={<EditOutlined />} 
                onClick={() => setIsConceptModalVisible(true)}
              >
                {form.getFieldValue('storyConcept') ? 'Edit Concept' : 'Define Concept'}
              </Button>
              {form.getFieldValue('storyConcept') ? (
                <Text type="success">✓ Concept defined ({form.getFieldValue('storyConcept')?.length} chars)</Text>
              ) : (
                <Text type="secondary">Not defined yet</Text>
              )}
            </div>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <Text strong>Target Audiences (Personas for AI Review)</Text>
            <Button 
              type="dashed" 
              icon={<RobotOutlined />} 
              onClick={handleSuggestAudiences}
              loading={isSuggestingAudiences}
            >
              AI Suggest Audiences
            </Button>
          </div>

          <Card size="small" styles={{ body: { padding: '12px' } }}>
            <Form.List name="targetAudiences">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'flex-start' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'code']}
                        rules={[{ required: true, message: 'Missing code' }]}
                        style={{ width: '120px', marginBottom: 0 }}
                      >
                        <Input placeholder="Code" />
                      </Form.Item>
                      <div style={{ flex: 1, display: 'flex', flexFlow: 'column', gap: '8px' }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          rules={[{ required: true, message: 'Missing name' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="Name (e.g. Young Parents)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'description']}
                          rules={[{ required: true, message: 'Missing description' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input.TextArea placeholder="Description/Persona details..." autoSize={{ minRows: 2 }} />
                        </Form.Item>
                      </div>
                      <Tooltip title="Remove audience">
                        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} style={{ marginTop: '4px' }} />
                      </Tooltip>
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Audience Manually
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          <Divider />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingStory ? 'Update Story' : 'Create Story'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <StoryConceptEditorModal
        open={isConceptModalVisible}
        onCancel={() => setIsConceptModalVisible(false)}
        initialValue={form.getFieldValue('storyConcept')}
        onSave={(concept) => {
          form.setFieldsValue({ storyConcept: concept });
          setIsConceptModalVisible(false);
        }}
      />
    </>
  );
};

export default SourceStoryModal;
